import { useState } from 'react';
import { motion } from 'framer-motion';
import SecurityBadge from './SecurityBadge';
import TermsDisclaimer from './TermsDisclaimer';

interface EmailInputFormProps {
  email: string;
  setEmail: (email: string) => void;
  error: string;
  setError: (error: string) => void;
  isSubmitting: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onSubmit: (e: React.FormEvent) => void;
}

const EmailInputForm = ({
  email,
  setEmail,
  error,
  setError,
  isSubmitting,
  inputRef,
  onSubmit
}: EmailInputFormProps) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <motion.div
      key="email-form"
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: 1,
        y: 0,
        transition: { 
          type: "spring", 
          stiffness: 400, 
          damping: 30
        }
      }}
      exit={{ 
        opacity: 0,
        y: -20, 
        transition: { 
          duration: 0.3,
          ease: [0.4, 0.0, 0.2, 1]
        } 
      }}
      className="px-8 pb-8"
    >
      <form onSubmit={onSubmit} className="mt-2">
        <div className="mb-5">
          <label htmlFor="email" className="block text-sm font-medium text-[#694e42] mb-2">
            Email Address
          </label>
          <div 
            className={`relative overflow-hidden rounded-xl border-2 ${
              error ? 'border-red-500' : 
              isFocused ? 'border-[#ff8c42]' : 
              'border-[#e2dad3]'
            } transition-all duration-300 shadow-sm hover:shadow-md`}
          >
            {/* Input glow effect when focused */}
            {isFocused && (
              <motion.div 
                className="absolute inset-0 bg-gradient-to-r from-[#ff8c42]/5 to-[#ff7a29]/5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            )}
            
            <input
              ref={inputRef}
              type="email"
              id="email"
              className="w-full px-5 py-4 focus:outline-none text-[#694e42] bg-transparent"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              disabled={isSubmitting}
            />
          </div>
          
          {/* Error message with animation */}
          {error && (
            <motion.div 
              className="mt-2 flex items-center text-red-500"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <p className="text-sm">{error}</p>
            </motion.div>
          )}
        </div>
        
        {/* Continue button */}
        <motion.button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3.5 px-6 rounded-xl font-medium text-white bg-gradient-to-r from-[#ff8c42] to-[#ff7a29] transition-all duration-300 shadow-md hover:shadow-lg focus:outline-none relative overflow-hidden"
          whileTap={{ scale: 0.98 }}
          whileHover={{ 
            boxShadow: "0 0 25px rgba(255, 140, 66, 0.6)"
          }}
        >
          {/* Glow effect on hover */}
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-[#ff8c42]/0 via-[#ff7a29]/50 to-[#ff8c42]/0 opacity-0"
            initial={{ opacity: 0, scale: 0.8, x: "-100%" }}
            whileHover={{ 
              opacity: 1, 
              scale: 1.2, 
              x: "100%",
              transition: { duration: 1.5, repeat: Infinity, repeatType: "loop", ease: "linear" }
            }}
          />
          <span className="relative z-10">Connect Email</span>
        </motion.button>
      </form>
      
      <SecurityBadge />
      <TermsDisclaimer />
    </motion.div>
  );
};

export default EmailInputForm;